import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { BookingWidget } from '../components/BookingWidget';
import { FAQSection } from '../components/FAQSection';
import { BookDirectBadge, DirectBookingBenefits } from '../components/BookingIncentives';
import { BookingInquiryModal } from '../components/BookingInquiryModal';
import { Bed, Bath, Users, Wifi, Coffee, Wind, Tv, Car, Check, ArrowRight, Mountain, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { SEOHead, BreadcrumbSchema, RoomSchema, SpeakableSchema } from '../components/SEO';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback rooms data in case API fails
const fallbackRooms = [
  {
    id: 'emerald-twin-queen',
    name: 'Emerald Twin Queen',
    type: 'standard',
    description: 'Comfortable twin room with queen-size bed, perfect for solo travelers or couples seeking a cozy retreat.',
    long_description: 'Our Emerald Twin Queen room offers the perfect blend of comfort and value. Featuring a plush queen-size bed, modern amenities, and elegant décor inspired by the surrounding Eastern Ghats. Wake up refreshed and ready to explore.',
    price: 2499,
    size: '250 sq ft',
    features: { beds: 1, bath: 1, guests: 1, bedType: 'Queen' },
    amenities: ['Queen Bed', 'Free WiFi', 'Air Conditioning', 'LED TV', 'Room Service', 'Attached Bathroom', 'Hot Water', 'Daily Housekeeping'],
    images: ['/images/gallery/bookingjini-room1.jpeg', '/images/gallery/hotel-7.jpeg'],
  },
  {
    id: 'emerald-crystal-deluxe',
    name: 'Emerald Crystal Deluxe',
    type: 'deluxe',
    description: 'Upgraded deluxe room with king bed, accommodating two guests with enhanced amenities.',
    long_description: 'Step into elegance with our Emerald Crystal Deluxe room. Designed for couples or friends traveling together, this room features a luxurious king bed, premium toiletries, and enhanced amenities for a memorable stay in the heart of Semiliguda.',
    price: 2499,
    size: '320 sq ft',
    features: { beds: 1, bath: 1, guests: 2, bedType: 'King' },
    amenities: ['King Bed', 'Free WiFi', 'Air Conditioning', '32" LED TV', 'Mini Fridge', 'Tea/Coffee Maker', 'Premium Toiletries', 'Wardrobe'],
    images: ['/images/gallery/hotel-4.jpeg', '/images/gallery/hotel-10.jpeg'],
  },
  {
    id: 'emerald-sapphire-suite',
    name: 'Emerald Sapphire Suite',
    type: 'suite',
    description: 'Our finest suite offering luxury accommodation with separate living area and premium services.',
    long_description: 'Indulge in the ultimate luxury at The Hotel Emerald with our Sapphire Suite. This expansive suite features a separate living area, premium king bed, and exclusive amenities. Perfect for special occasions or guests seeking the finest experience.',
    price: 6999,
    size: '550 sq ft',
    features: { beds: 1, bath: 2, guests: 2, bedType: 'King' },
    amenities: ['King Bed', 'Living Room', 'Panoramic View', 'Premium WiFi', 'Air Conditioning', '55" Smart TV', 'Mini Bar', 'Jacuzzi Tub', 'Butler Service', 'Welcome Amenities'],
    images: ['/images/gallery/hotel-34.jpeg', '/images/gallery/hotel-24.jpeg'],
  },
];

export default function RoomsPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomTypes, setRoomTypes] = useState([{ value: 'all', label: 'All Rooms' }]);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/rooms`);
        if (response.data && response.data.length > 0) {
          // Map API response to expected format
          const mappedRooms = response.data.map(room => ({
            ...room,
            longDescription: room.long_description || room.description,
            features: {
              ...room.features,
              bedType: room.features?.bedType || 'King'
            }
          }));
          setRooms(mappedRooms);
          
          // Generate dynamic room types from data
          const typesSet = new Set(mappedRooms.map(room => room.type?.toLowerCase()));
          const dynamicTypes = [
            { value: 'all', label: 'All Rooms' },
            ...Array.from(typesSet)
              .filter(Boolean)
              .sort()
              .map(type => ({
                value: type,
                label: type.charAt(0).toUpperCase() + type.slice(1)
              }))
          ];
          setRoomTypes(dynamicTypes);
        } else {
          setRooms(fallbackRooms);
        }
      } catch (error) {
        console.error('Failed to fetch rooms:', error);
        setRooms(fallbackRooms);
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, []);

  const filteredRooms = activeFilter === 'all' 
    ? rooms 
    : rooms.filter(room => room.type?.toLowerCase() === activeFilter);

  const handleBookRoom = (room) => {
    setSelectedRoom(room.id);
    setShowInquiryModal(true);
  };

  const breadcrumbs = [
    { name: 'Home', url: 'https://thehotelemerald.in/' },
    { name: 'Rooms & Suites', url: 'https://thehotelemerald.in/rooms' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="rooms-page">
      <SEOHead 
        title="Rooms & Suites | The Hotel Emerald - Luxury Accommodation in Semiliguda"
        description="Explore our luxurious rooms and suites at The Hotel Emerald. From Standard rooms at ₹2,499 to Presidential Suites. Book direct for best rates."
        keywords="hotel rooms semiliguda, luxury suites odisha, deluxe rooms koraput, hotel emerald rooms"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      {rooms.map(room => <RoomSchema key={room.id} room={room} />)}
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/gallery/hotel-4.jpeg"
            alt="Luxury Suite"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-950/60" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="overline text-gold mb-3"
          >
            Accommodations
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4"
          >
            Rooms & Suites
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-emerald-100 max-w-xl mx-auto"
          >
            Discover your perfect sanctuary amidst the Eastern Ghats
          </motion.p>
        </div>
      </section>

      {/* Booking Widget */}
      <div className="container-custom -mt-8 sm:-mt-12 lg:-mt-16 relative z-20 px-3 sm:px-6 lg:px-8">
        <BookingWidget variant="compact" />
      </div>

      {/* Price Range Info */}
      <section className="py-6 sm:py-8 bg-emerald-950 text-white mt-4 sm:mt-6 lg:mt-8">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-4 sm:gap-8 text-center">
            <div>
              <p className="text-emerald-300 text-xs sm:text-sm">Starting From</p>
              <p className="text-2xl sm:text-3xl font-serif">₹2,499<span className="text-base sm:text-lg text-emerald-300">/night</span></p>
            </div>
            <div className="hidden sm:block h-12 w-px bg-emerald-700" />
            <div>
              <p className="text-emerald-300 text-xs sm:text-sm">Room Categories</p>
              <p className="text-lg sm:text-xl font-serif">{roomTypes.length - 1} Room Types</p>
            </div>
            <div className="hidden sm:block h-12 w-px bg-emerald-700" />
            <div className="px-4">
              <p className="text-emerald-300 text-xs sm:text-sm">Best Price Guarantee</p>
              <p className="text-xs sm:text-sm">Lower than Booking.com, Expedia & more</p>
            </div>
          </div>
        </div>
      </section>

      {/* Rooms Filter */}
      <section className="section-padding">
        <div className="container-custom">
          {/* Filter Tabs */}
          <div className="flex justify-center mb-12">
            <Tabs value={activeFilter} onValueChange={setActiveFilter}>
              <TabsList className="bg-muted">
                {roomTypes.map((type) => (
                  <TabsTrigger 
                    key={type.value} 
                    value={type.value}
                    data-testid={`filter-${type.value}`}
                  >
                    {type.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading rooms...</p>
            </div>
          )}

          {/* Rooms List */}
          {!loading && (
          <div className="space-y-16">
            {filteredRooms.map((room, index) => (
              <motion.div
                key={room.id}
                id={room.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                data-testid={`room-detail-${room.id}`}
              >
                {/* Images */}
                <div className={`relative ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="aspect-[4/3] rounded-sm overflow-hidden">
                    <img
                      src={room.images?.[0] || '/images/gallery/hotel-4.jpeg'}
                      alt={room.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  {room.images?.[1] && (
                  <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-sm overflow-hidden border-4 border-background shadow-xl hidden md:block">
                    <img
                      src={room.images[1]}
                      alt={`${room.name} alternate view`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  )}
                  
                  {/* Badges */}
                  <div className="absolute top-4 left-4 flex gap-2">
                    <BookDirectBadge variant="corner" />
                    {room.featured && (
                      <Badge className="bg-gold text-emerald-950">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Popular
                      </Badge>
                    )}
                    {room.availability && typeof room.availability === 'number' && room.availability <= 2 && (
                      <Badge variant="destructive">
                        Only {room.availability} left
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''} lg:pl-8`}>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="overline text-emerald-700 dark:text-emerald-400">{room.size}</p>
                    <span className="text-muted-foreground">•</span>
                    <p className="overline text-emerald-700 dark:text-emerald-400 capitalize">{room.type}</p>
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">{room.name}</h2>
                  <p className="text-muted-foreground mb-6 leading-relaxed">{room.longDescription}</p>

                  {/* Features */}
                  <div className="flex items-center gap-6 mb-6 text-muted-foreground">
                    <span className="flex items-center gap-2">
                      <Bed className="w-5 h-5" />
                      {room.features.bedType} Bed
                    </span>
                    <span className="flex items-center gap-2">
                      <Bath className="w-5 h-5" />
                      {room.features.bath} Bath
                    </span>
                    <span className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {room.features.guests} {room.features.guests === 1 ? 'Guest' : 'Guests'}
                    </span>
                  </div>

                  {/* Amenities */}
                  <div className="grid grid-cols-2 gap-2 mb-8">
                    {room.amenities.slice(0, 6).map((amenity) => (
                      <div key={amenity} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Check className="w-4 h-4 text-emerald-600" />
                        {amenity}
                      </div>
                    ))}
                  </div>

                  {/* Price and CTA */}
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-3xl font-serif text-foreground">
                        ₹{room.price.toLocaleString()}
                        <span className="text-sm text-muted-foreground font-sans">/night</span>
                      </p>
                      <p className="text-xs text-muted-foreground">Exclusive of taxes</p>
                    </div>
                    <Button 
                      className="btn-emerald"
                      data-testid={`book-room-${room.id}`}
                      onClick={() => handleBookRoom(room)}
                    >
                      Book Now
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Why Book Direct */}
      <section className="section-padding bg-emerald-950 text-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="overline text-gold mb-3">Best Rate Guarantee</p>
            <h2 className="font-serif text-3xl md:text-4xl">Why Book Direct?</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-16 h-16 mx-auto mb-4 bg-emerald-800 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-gold" />
              </div>
              <h3 className="font-serif text-xl mb-2">Lowest Price</h3>
              <p className="text-emerald-300 text-sm">Our rates are lower than Booking.com, Agoda, Expedia & GoIbibo</p>
            </div>
            <div>
              <div className="w-16 h-16 mx-auto mb-4 bg-emerald-800 rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-gold" />
              </div>
              <h3 className="font-serif text-xl mb-2">Exclusive Perks</h3>
              <p className="text-emerald-300 text-sm">Complimentary welcome drink and room upgrade subject to availability</p>
            </div>
            <div>
              <div className="w-16 h-16 mx-auto mb-4 bg-emerald-800 rounded-full flex items-center justify-center">
                <Mountain className="w-8 h-8 text-gold" />
              </div>
              <h3 className="font-serif text-xl mb-2">Flexible Cancellation</h3>
              <p className="text-emerald-300 text-sm">Free cancellation up to 24 hours before check-in on select rates</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section for Rooms */}
      <FAQSection category="rooms" title="Room & Booking FAQs" />

      <Footer />

      {/* Booking Inquiry Modal */}
      <BookingInquiryModal 
        open={showInquiryModal} 
        onOpenChange={setShowInquiryModal}
        prefillRoom={selectedRoom}
        source="rooms_page"
      />
    </div>
  );
}
